/** @jsx jsx */
/** @jsxRuntime classic */
import { Interpolation, jsx, keyframes } from '@emotion/react';
import React, { forwardRef } from 'react';
import { ThemeConfig } from '../style/theme';
import useTheme from '../style/useTheme';

export type Props = {
  className?: string;
  ['data-testid']?: string;
  progress?: number;
  variant?: 'determinate' | 'indeterminate';
};

const indeterminate1 = keyframes`
  0% {
    left: -35%;
    right: 100%;
  }

  60% {
    left: 100%;
    right: -90%;
  }

  100% {
    left: 100%;
    right: -90%;
  }
`;

const indeterminate2 = keyframes`
  0% {
    left: -200%;
    right: 100%;
  }

  60% {
    left: -200%;
    right: 100%;
  }

  100% {
    left: 107%;
    right: -8%;
  }
`;

const determinateGradient = keyframes`
  0% {
    left: -200%;
    right: 100%;
  }

  60% {
    left: 107%;
    right: -8%;
  }

  100% {
    left: 107%;
    right: -8%;
  }
`;

const IndeterminateLinearProgress = () => {
  const { theme, resolveColor } = useTheme();
  const baseCSS: Interpolation<ThemeConfig> = {
    backgroundColor: resolveColor(theme.components.loading.color),
    position: 'absolute',
    left: 0,
    bottom: 0,
    top: 0,
    transition: 'transform 0.2s linear',
    transformOrigin: 'left',
  };
  return (
    <React.Fragment>
      <span
        css={{
          ...baseCSS,
          animation: `${indeterminate1} 2s cubic-bezier(0.65, 0.815, 0.735, 0.395) infinite;`,
        }}
      />
      <span
        css={{
          ...baseCSS,
          animation: `${indeterminate2} 2s cubic-bezier(0.165, 0.84, 0.44, 1) infinite;`,
        }}
      />
    </React.Fragment>
  );
};

const DeterminateLinearProgress: React.FC<Props> = ({ progress }) => {
  const { theme, resolveColor } = useTheme();
  const actualProgress =
    // progress not defined or < 0 = 0
    // progress > 100 = 0
    // eslint-disable-next-line no-nested-ternary
    !progress || progress < 0 ? 0 : progress > 100 ? 100 : progress;

  const ariaProps = {
    'aria-valuenow': Math.round(actualProgress),
    'aria-valuemin': 0,
    'aria-valuemax': 100,
  };

  const baseCSS: Interpolation<ThemeConfig> = {
    width: '100%',
    position: 'absolute',
    left: 0,
    bottom: 0,
    top: 0,
    transition: 'transform 0.2s linear',
    borderRadius: '8px',
    transformOrigin: 'left',
  };

  return (
    <React.Fragment>
      <span
        role="progressbar"
        css={{
          ...baseCSS,
          backgroundColor: resolveColor(theme.components.loading.color),
          overflow: 'hidden',
          transform: `translateX(-${100 - actualProgress}%)`,
        }}
        {...ariaProps}
      >
        <span
          css={{
            ...baseCSS,
            background:
              'linear-gradient(90deg, rgba(255, 255, 255, 0) 30.8%, rgba(255, 255, 255, 0.25) 48.03%, rgba(255, 255, 255, 0) 65.79%)',
            animation: `${determinateGradient} 2s cubic-bezier(0.165, 0.84, 0.44, 1) 1.12s infinite`,
          }}
        />
      </span>
    </React.Fragment>
  );
};

const LinearProgress = forwardRef<HTMLDivElement, Props>((props, ref) => {
  const { theme, resolveColor } = useTheme();
  return (
    <div
      ref={ref}
      data-testid={props['data-testid'] || 'LinearProgress'}
      className="origami-LinearProgress"
      css={{
        width: '100%',
        height: '8px',
        backgroundColor: resolveColor(
          theme.components.loading['background-color']
        ),
        borderRadius: '8px',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {props.variant === 'determinate' ? (
        <DeterminateLinearProgress {...props} />
      ) : (
        <IndeterminateLinearProgress />
      )}
    </div>
  );
});

export default LinearProgress;
